// store/slices/visiteSlice.js
import {createAsyncThunk, createSlice} from '@reduxjs/toolkit';
import api from "../../services/axios.js";
import _ from 'lodash';

// Thunks pour les visites
export const fetchVisites = createAsyncThunk(
    'visites/fetchVisites',
    async ({ siteId, params = {} }, { rejectWithValue }) => {
        try {
            const { page = 1, limit = 20, startDate, endDate } = params;
            const response = await api.get(`/api/sites/${siteId}/visites`, {
                params: { page, limit, startDate, endDate }
            });
            return { siteId, ...response.data };
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const fetchAllVisites = createAsyncThunk(
    'visites/fetchAllVisites',
    async (params = {}, { rejectWithValue }) => {
        try {
            const { page = 1, limit = 50, startDate, endDate, search } = params;
            const response = await api.get('/api/visites', {
                params: { page, limit, startDate, endDate, search }
            });
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const getVisiteStats = createAsyncThunk(
    'visites/getVisiteStats',
    async (siteId, { rejectWithValue }) => {
        try {
            const response = await api.get(`/api/visites/${siteId}/stats`);
            return { siteId, ...response.data };
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const getVisiteGeoStats = createAsyncThunk(
    'visites/getVisiteGeoStats',
    async (siteId, { rejectWithValue }) => {
        try {
            const response = await api.get(`/api/visites/${siteId}/geo-stats`);
            return { siteId, ...response.data };
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const getDeviceStats = createAsyncThunk(
    'visites/getDeviceStats',
    async (siteId, { rejectWithValue }) => {
        try {
            const response = await api.get(`/api/visites/${siteId}/device-stats`);
            return { siteId, ...response.data };
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const getTimeStats = createAsyncThunk(
    'visites/getTimeStats',
    async (siteId, { rejectWithValue }) => {
        try {
            const response = await api.get(`/api/visites/${siteId}/time-stats`);
            return { siteId, ...response.data };
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const deleteVisite = createAsyncThunk(
    'visites/deleteVisite',
    async (id, { rejectWithValue }) => {
        try {
            await api.delete(`/api/visites/${id}`);
            return id;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const deleteVisitesBySite = createAsyncThunk(
    'visites/deleteVisitesBySite',
    async (siteId, { rejectWithValue }) => {
        try {
            await api.delete(`/api/visites/site/${siteId}`);
            return siteId;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const batchDeleteVisites = createAsyncThunk(
    'visites/batchDeleteVisites',
    async (visiteIds, { rejectWithValue }) => {
        try {
            const response = await api.post('/api/visites/batch-delete', { visiteIds });
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

// Filtrage local (optionnel)
export const filterVisites = createAsyncThunk(
    'visites/filterVisites',
    async (filters, { getState }) => {
        const { visites } = getState();
        const allVisites = _.get(visites, 'currentSiteVisites.data', []);

        return _.filter(allVisites, (visite) => {
            let match = true;

            if (filters.country) {
                match = match && _.includes(
                    _.toLower(_.get(visite, 'country', '')),
                    _.toLower(filters.country)
                );
            }

            if (filters.city) {
                match = match && _.includes(
                    _.toLower(_.get(visite, 'city', '')),
                    _.toLower(filters.city)
                );
            }

            if (filters.deviceType) {
                match = match && _.get(visite, 'deviceType') === filters.deviceType;
            }

            if (filters.browser) {
                match = match && _.includes(
                    _.toLower(_.get(visite, 'browser', '')),
                    _.toLower(filters.browser)
                );
            }

            if (filters.startDate) {
                const visiteDate = new Date(_.get(visite, 'visitedAt'));
                const startDate = new Date(filters.startDate);
                match = match && visiteDate >= startDate;
            }

            if (filters.endDate) {
                const visiteDate = new Date(_.get(visite, 'visitedAt'));
                const endDate = new Date(filters.endDate);
                endDate.setHours(23, 59, 59, 999);
                match = match && visiteDate <= endDate;
            }

            return match;
        });
    }
);

const visiteSlice = createSlice({
    name: 'visites',
    initialState: {
        // Visites pour un site spécifique
        currentSiteVisites: {
            data: [],
            pagination: {
                page: 1,
                limit: 20,
                total: 0,
                pages: 0
            }
        },

        // Toutes les visites (pour l'admin)
        allVisites: {
            data: [],
            pagination: {
                page: 1,
                limit: 50,
                total: 0,
                pages: 0
            }
        },

        // Statistiques
        stats: null,
        geoStats: null,
        deviceStats: null,
        timeStats: null,

        // États de chargement
        loading: false,
        loadingStats: false,
        loadingGeoStats: false,
        loadingDeviceStats: false,
        loadingTimeStats: false,

        // Filtres
        filters: {
            country: '',
            city: '',
            deviceType: '',
            browser: '',
            startDate: '',
            endDate: '',
            search: ''
        },

        // Données filtrées localement
        filteredVisites: [],

        // Erreurs
        error: null,
        deleteError: null,

        // Métriques en temps réel
        realtimeMetrics: {
            onlineVisitors: 0,
            todayVisits: 0,
            lastHourVisits: 0,
            topCountries: [],
            activeDevices: []
        }
    },
    reducers: {
        setFilters: (state, action) => {
            state.filters = { ...state.filters, ...action.payload };
        },
        clearFilters: (state) => {
            state.filters = {
                country: '',
                city: '',
                deviceType: '',
                browser: '',
                startDate: '',
                endDate: '',
                search: ''
            };
            state.filteredVisites = [];
        },
        clearCurrentSiteVisites: (state) => {
            state.currentSiteVisites = {
                data: [],
                pagination: {
                    page: 1,
                    limit: 20,
                    total: 0,
                    pages: 0
                }
            };
            state.stats = null;
            state.geoStats = null;
            state.deviceStats = null;
            state.timeStats = null;
        },
        clearErrors: (state) => {
            state.error = null;
            state.deleteError = null;
        },

        // Actions pour les mises à jour temps réel
        addNewVisite: (state, action) => {
            const newVisite = action.payload;

            // Ajouter au début de la liste actuelle
            state.currentSiteVisites.data.unshift(newVisite);
            state.currentSiteVisites.total += 1;

            // Mettre à jour les statistiques
            if (state.stats) {
                state.stats.totalVisits += 1;
                state.stats.todayVisits += 1;
            }

            // Mettre à jour les métriques temps réel
            state.realtimeMetrics.todayVisits += 1;
            state.realtimeMetrics.lastHourVisits += 1;

            // Mettre à jour les top pays
            const country = _.get(newVisite, 'country', 'Inconnu');
            const countryIndex = _.findIndex(state.realtimeMetrics.topCountries, { country });

            if (countryIndex > -1) {
                state.realtimeMetrics.topCountries[countryIndex].count += 1;
            } else if (state.realtimeMetrics.topCountries.length < 5) {
                state.realtimeMetrics.topCountries.push({
                    country,
                    count: 1,
                    countryCode: _.get(newVisite, 'countryCode')
                });
            }

            // Mettre à jour les devices actifs
            const deviceType = _.get(newVisite, 'deviceType', 'desktop');
            const deviceIndex = _.findIndex(state.realtimeMetrics.activeDevices, { type: deviceType });

            if (deviceIndex > -1) {
                state.realtimeMetrics.activeDevices[deviceIndex].count += 1;
            } else {
                state.realtimeMetrics.activeDevices.push({
                    type: deviceType,
                    count: 1
                });
            }
        },

        updateRealtimeMetrics: (state, action) => {
            state.realtimeMetrics = {
                ...state.realtimeMetrics,
                ...action.payload
            };
        },

        setOnlineVisitors: (state, action) => {
            state.realtimeMetrics.onlineVisitors = action.payload;
        },

        // Utilitaires pour formater les données
        formatVisiteForDisplay: (state, action) => {
            // Cette fonction pourrait formater les données pour l'affichage
            return state;
        }
    },
    extraReducers: (builder) => {
        builder
            // fetchVisites
            .addCase(fetchVisites.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchVisites.fulfilled, (state, action) => {
                state.loading = false;
                state.currentSiteVisites = {
                    data: _.get(action.payload, 'data', []),
                    pagination: _.get(action.payload, 'pagination', {
                        page: 1,
                        limit: 20,
                        total: 0,
                        pages: 0
                    })
                };
            })
            .addCase(fetchVisites.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || action.error.message;
            })

            // fetchAllVisites
            .addCase(fetchAllVisites.pending, (state) => {
                state.loading = true;
            })
            .addCase(fetchAllVisites.fulfilled, (state, action) => {
                state.loading = false;
                state.allVisites = {
                    data: _.get(action.payload, 'data', []),
                    pagination: _.get(action.payload, 'pagination', {
                        page: 1,
                        limit: 50,
                        total: 0,
                        pages: 0
                    })
                };
            })

            // getVisiteStats
            .addCase(getVisiteStats.pending, (state) => {
                state.loadingStats = true;
            })
            .addCase(getVisiteStats.fulfilled, (state, action) => {
                state.loadingStats = false;
                state.stats = action.payload;
            })
            .addCase(getVisiteStats.rejected, (state, action) => {
                state.loadingStats = false;
                state.error = action.payload || action.error.message;
            })

            // getVisiteGeoStats
            .addCase(getVisiteGeoStats.pending, (state) => {
                state.loadingGeoStats = true;
            })
            .addCase(getVisiteGeoStats.fulfilled, (state, action) => {
                state.loadingGeoStats = false;
                state.geoStats = action.payload;
            })

            // getDeviceStats
            .addCase(getDeviceStats.pending, (state) => {
                state.loadingDeviceStats = true;
            })
            .addCase(getDeviceStats.fulfilled, (state, action) => {
                state.loadingDeviceStats = false;
                state.deviceStats = action.payload;
            })

            // getTimeStats
            .addCase(getTimeStats.pending, (state) => {
                state.loadingTimeStats = true;
            })
            .addCase(getTimeStats.fulfilled, (state, action) => {
                state.loadingTimeStats = false;
                state.timeStats = action.payload;
            })

            // deleteVisite
            .addCase(deleteVisite.pending, (state) => {
                state.deleteError = null;
            })
            .addCase(deleteVisite.fulfilled, (state, action) => {
                // Retirer de toutes les listes
                const visiteId = action.payload;

                // Liste actuelle
                state.currentSiteVisites.data = _.filter(
                    state.currentSiteVisites.data,
                    visite => _.get(visite, 'id') !== visiteId
                );
                state.currentSiteVisites.total -= 1;

                // Toutes les visites
                state.allVisites.data = _.filter(
                    state.allVisites.data,
                    visite => _.get(visite, 'id') !== visiteId
                );
                state.allVisites.total -= 1;

                // Visites filtrées
                state.filteredVisites = _.filter(
                    state.filteredVisites,
                    visite => _.get(visite, 'id') !== visiteId
                );
            })
            .addCase(deleteVisite.rejected, (state, action) => {
                state.deleteError = action.payload;
            })

            // deleteVisitesBySite
            .addCase(deleteVisitesBySite.fulfilled, (state, action) => {
                const siteId = action.payload;
                // Réinitialiser si c'est le site actuel
                if (state.currentSiteVisites.siteId === siteId) {
                    state.currentSiteVisites = {
                        data: [],
                        pagination: {
                            page: 1,
                            limit: 20,
                            total: 0,
                            pages: 0
                        }
                    };
                }
            })

            // filterVisites
            .addCase(filterVisites.fulfilled, (state, action) => {
                state.filteredVisites = action.payload;
            })

            // batchDeleteVisites
            .addCase(batchDeleteVisites.fulfilled, (state, action) => {
                const deletedIds = _.get(action.payload, 'deletedIds', []);

                if (!_.isEmpty(deletedIds)) {
                    // Filtrer les listes
                    const filterByIds = (visites) =>
                        _.filter(visites, visite => !_.includes(deletedIds, _.get(visite, 'id')));

                    state.currentSiteVisites.data = filterByIds(state.currentSiteVisites.data);
                    state.currentSiteVisites.total = state.currentSiteVisites.data.length;

                    state.allVisites.data = filterByIds(state.allVisites.data);
                    state.allVisites.total = state.allVisites.data.length;

                    state.filteredVisites = filterByIds(state.filteredVisites);
                }
            });
    }
});

export const {
    setFilters,
    clearFilters,
    clearCurrentSiteVisites,
    clearErrors,
    addNewVisite,
    updateRealtimeMetrics,
    setOnlineVisitors,
    formatVisiteForDisplay
} = visiteSlice.actions;

// Sélecteurs
export const selectCurrentSiteVisites = (state) => state.visites.currentSiteVisites;
export const selectAllVisites = (state) => state.visites.allVisites;
export const selectVisiteStats = (state) => state.visites.stats;
export const selectGeoStats = (state) => state.visites.geoStats;
export const selectDeviceStats = (state) => state.visites.deviceStats;
export const selectTimeStats = (state) => state.visites.timeStats;
export const selectFilteredVisites = (state) => state.visites.filteredVisites;
export const selectFilters = (state) => state.visites.filters;
export const selectRealtimeMetrics = (state) => state.visites.realtimeMetrics;
export const selectLoading = (state) => state.visites.loading;
export const selectLoadingStats = (state) => state.visites.loadingStats;
export const selectLoadingGeoStats = (state) => state.visites.loadingGeoStats;
export const selectLoadingDeviceStats = (state) => state.visites.loadingDeviceStats;
export const selectLoadingTimeStats = (state) => state.visites.loadingTimeStats;
export const selectVisiteErrors = (state) => ({
    error: state.visites.error,
    deleteError: state.visites.deleteError
});

// Sélecteurs dérivés avec lodash
export const selectVisitesGroupedByCountry = (state) => {
    const visites = _.get(state.visites.currentSiteVisites, 'data', []);
    return _.chain(visites)
        .groupBy(visite => _.get(visite, 'country', 'Inconnu'))
        .mapValues((group, country) => ({
            count: _.size(group),
            countryCode: _.get(_.first(group), 'countryCode'),
            visitors: _.take(group, 10)
        }))
        .value();
};

export const selectVisitesGroupedByDevice = (state) => {
    const visites = _.get(state.visites.currentSiteVisites, 'data', []);
    return _.chain(visites)
        .groupBy(visite => _.get(visite, 'deviceType', 'desktop'))
        .mapValues((group, deviceType) => ({
            count: _.size(group),
            percentage: (_.size(group) / _.size(visites)) * 100
        }))
        .value();
};

export const selectVisitesByDate = (state) => {
    const visites = _.get(state.visites.currentSiteVisites, 'data', []);
    return _.chain(visites)
        .groupBy(visite => {
            const date = new Date(_.get(visite, 'visitedAt'));
            return date.toISOString().split('T')[0]; // YYYY-MM-DD
        })
        .mapValues((group, date) => ({
            count: _.size(group),
            date
        }))
        .values()
        .orderBy(['date'], ['desc'])
        .take(30)
        .value();
};

export const selectTopBrowsers = (state) => {
    const visites = _.get(state.visites.currentSiteVisites, 'data', []);
    return _.chain(visites)
        .countBy(visite => _.get(visite, 'browser', 'Inconnu'))
        .toPairs()
        .sortBy(([, count]) => -count)
        .take(5)
        .fromPairs()
        .value();
};

export const selectTopReferrers = (state) => {
    const visites = _.get(state.visites.currentSiteVisites, 'data', []);
    return _.chain(visites)
        .filter(visite => _.get(visite, 'referrer'))
        .countBy(visite => {
            const referrer = _.get(visite, 'referrer');
            try {
                const url = new URL(referrer);
                return url.hostname;
            } catch {
                return referrer;
            }
        })
        .toPairs()
        .sortBy(([, count]) => -count)
        .take(10)
        .fromPairs()
        .value();
};

export default visiteSlice.reducer;